import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * Custom hook to fetch categories from API
 * @param {string} listingType - Type of listing (product, service, rental, etc.)
 * @returns {Object} - { categories, subcategories, loading, error, getCategoryById, getSubcategories }
 */
export const useCategories = (listingType) => {
  const { apiCall } = useAuth();
  const [categories, setCategories] = useState([]);       // ACTIVE only — used by dropdowns.
  const [allCategories, setAllCategories] = useState([]); // ACTIVE + inactive — used by name→ID lookups.
  const [categoryMap, setCategoryMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!listingType) {
      setLoading(false);
      return;
    }

    fetchCategories();
  }, [listingType]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      // iter-294 Bug-2 fix — fetch BOTH active and inactive categories.
      //
      // Prior behaviour: this hook fetched `is_active=true` only. Existing
      // listings imported against categories that were later marked
      // `is_active=false` in CMS (taxonomy reorg, seasonal archival, etc.)
      // then failed name→ID lookup on the Edit form. The FilterAttributesCard
      // consequently returned `null` and vendors/admins saw "no Filters
      // section" for those listings — the exact Hare Krishna Enterprises
      // pattern.
      //
      // Fix: fetch the full taxonomy (no `is_active` filter). Split into:
      //   • `categories`     — active-only, drives the dropdown so new
      //                        selections stay within the current taxonomy.
      //   • `allCategories`  — full list, used by getCategoryByName /
      //                        getSubcategoryByName for existing listings
      //                        whose stored category name may reference an
      //                        archived taxonomy entry.
      const data = await apiCall(`/api/categories/?listing_type=${listingType}`);

      // Sort by sort_order for stable rendering.
      const sortedAll = data.sort((a, b) => a.sort_order - b.sort_order);
      const sortedActive = sortedAll.filter(cat => cat.is_active !== false);

      setAllCategories(sortedAll);
      setCategories(sortedActive);

      // Map for subcategory lookup: keyed on category NAME. Include both
      // active AND inactive categories so listings with archived-parent
      // subcategories still resolve.
      const map = {};
      sortedAll.forEach(cat => {
        const sortedSubcats = (cat.subcategories || []).sort((a, b) => a.sort_order - b.sort_order);
        map[cat.name] = sortedSubcats;
      });

      setCategoryMap(map);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
      setError(err.message || 'Failed to load categories');
      setCategories([]);
      setAllCategories([]);
      setCategoryMap({});
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get category object by name.
   *
   * iter-294 Bug-2 fix — searches `allCategories` (active + inactive)
   * so listings whose stored category was later archived in CMS still
   * resolve to a valid ID. This is the PRIMARY fix for the "Filters
   * section missing on Hare Krishna Enterprises listings" report.
   *
   * The normalization fallback remains for a smaller secondary drift
   * class (whitespace / casing / HTML-entity encoding). It is
   * deliberately narrow: trim + lowercase + decode a small set of
   * common HTML entities. It does NOT strip punctuation or collapse
   * whitespace inside the name so distinct categories like
   * "Home & Kitchen" vs "Home Kitchen" stay disjoint.
   */
  const _normalizeCatName = (s) => {
    if (!s || typeof s !== 'string') return '';
    return s
      .trim()
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/&#39;/g, "'")
      .replace(/&quot;/g, '"');
  };

  const getCategoryByName = (categoryName) => {
    if (!categoryName) return undefined;
    // 1) Fast path — strict equality against the FULL taxonomy list.
    const strict = allCategories.find(cat => cat.name === categoryName);
    if (strict) return strict;
    // 2) Fallback — normalized comparison for whitespace/case/entity drift.
    const needle = _normalizeCatName(categoryName);
    if (!needle) return undefined;
    return allCategories.find(cat => _normalizeCatName(cat.name) === needle);
  };

  /**
   * Get subcategories for a category.
   *
   * iter-294 Bug-2 fix — same normalization as getCategoryByName so
   * subcategories load correctly for listings whose stored category
   * name has drifted from the current taxonomy.
   */
  const getSubcategories = (categoryName) => {
    if (!categoryName) return [];
    // 1) Fast path — strict key hit.
    const strict = categoryMap[categoryName];
    if (strict) return strict;
    // 2) Fallback — normalized key lookup.
    const needle = _normalizeCatName(categoryName);
    if (!needle) return [];
    for (const k of Object.keys(categoryMap)) {
      if (_normalizeCatName(k) === needle) return categoryMap[k] || [];
    }
    return [];
  };

  /**
   * Get category names only (for dropdown)
   */
  const getCategoryNames = () => {
    return categories.map(cat => cat.name);
  };

  /**
   * Get subcategory names only (for dropdown)
   */
  const getSubcategoryNames = (categoryName) => {
    const subcats = getSubcategories(categoryName);
    return subcats.map(sub => sub.name);
  };

  /**
   * Get subcategory object by name.
   *
   * iter-294 Bug-2 fix — normalized lookup on the subcategory name too.
   */
  const getSubcategoryByName = (categoryName, subcategoryName) => {
    const subcats = getSubcategories(categoryName);
    if (!subcategoryName) return undefined;
    const strict = subcats.find(sub => sub.name === subcategoryName);
    if (strict) return strict;
    const needle = _normalizeCatName(subcategoryName);
    if (!needle) return undefined;
    return subcats.find(sub => _normalizeCatName(sub.name) === needle);
  };

  return {
    categories,
    categoryMap,
    loading,
    error,
    getCategoryByName,
    getSubcategories,
    getCategoryNames,
    getSubcategoryNames,
    getSubcategoryByName,
    refetch: fetchCategories
  };
};

export default useCategories;
